import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  cn,
  CollapsibleContainer,
  Heading,
  Icon,
  Paragraph,
  Spinner,
} from '@centrix/shared-ui';
import { checkCircleIcon, errorFillIcon } from '@centrix/shared-ui/src/icons';
import { ApiError, apiClient, useCurrentUser } from '@centrix/api-client';

import logoImage from '@/assets/logo.png';

import {
  buildOnboardingPath,
  clearSignupInFlight,
  isEmailMarkedVerified,
  isSignupInFlight,
  markEmailVerified,
  resolveOnboardingEmail,
  resolveOnboardingToken,
  setRegistrationType,
} from './onboarding';
import { OTP_LENGTH, OtpInput } from './OtpInput';

const MISSING_EMAIL_MESSAGE =
  'We could not determine which email to verify. Please restart the signup flow.';
const FALLBACK_EMAIL_LABEL = 'your email';
const VERIFY_ERROR_MESSAGE = 'Failed to authenticate. Invalid OTP';
const DEFAULT_RESEND_ERROR = 'Could not send a new OTP. Please try again in a moment.';
const RESEND_SUCCESS_MESSAGE = 'A new OTP has been sent to your email.';
const SUCCESS_REDIRECT_DELAY_MS = 1200;

async function verifyOtp(email: string, otpCode: string): Promise<{ registration_type: string }> {
  const response = await apiClient.post('/auth/verify-email/', { email, otp_code: otpCode });
  return response.data;
}

async function resendOtp(email: string): Promise<void> {
  await apiClient.post('/auth/resend-otp/', { email });
}

export const VerifyEmailPage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: currentUser } = useCurrentUser();

  const email = resolveOnboardingEmail({
    locationState: location.state,
    searchParams,
    currentUser,
  });

  const token = resolveOnboardingToken({
    locationState: location.state,
    searchParams,
  });

  const hasEmail = Boolean(email);

  const [otp, setOtp] = useState<string[]>(() => Array(OTP_LENGTH).fill(''));
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(() => isEmailMarkedVerified(email));
  const [registrationType, setRegistrationType] = useState('');
  // Captured once at mount — true when email was already verified before this
  // render cycle (back/forward navigation). Guards A and B use this to avoid
  // racing: Guard A fires immediately for back/forward; Guard B fires after the
  // 1200ms success animation for fresh OTP verification.
  const [initiallyVerified] = useState(() => isEmailMarkedVerified(email));

  const otpCode = otp.join('');
  const isOtpComplete = otpCode.length === OTP_LENGTH && otp.every(Boolean);

  // Guard A: if the email was already verified when this component mounted
  // (e.g. browser Back/Forward after a completed OTP), redirect immediately so
  // the OTP screen does not remain accessible in history.
  // Uses initiallyVerified (captured once at mount) so this effect never fires
  // after a fresh OTP verification — Guard B (timer below) handles that case.
  useEffect(() => {
    if (!hasEmail || !initiallyVerified) return;
    navigate(
      buildOnboardingPath('/signup/select-sectors', { email, params: { token, registrationType } }),
      {
        state: { email, token, registrationType },
        replace: true,
      }
    );
  }, [hasEmail, initiallyVerified, email, token, navigate, registrationType]);

  // The OTP page is reachable only as the immediate step after a fresh signup.
  // If there is no in-flight signup (back/forward navigation after leaving the
  // page, direct URL visit, bookmark) bounce the user back to /signup so they
  // must complete a new registration to receive a new OTP.
  useEffect(() => {
    if (isSignupInFlight() || isEmailMarkedVerified(email)) return;
    clearSignupInFlight();
    navigate('/signup', { replace: true });
  }, [email, navigate]);

  // Auto-redirect after success. Replace the verify page in history so the
  // browser Back button cannot return to a stale "Email Verified" state and
  // re-submit an OTP that the backend has already consumed.
  useEffect(() => {
    if (!verified || !registrationType) return;
    const timer = setTimeout(() => {
      navigate(
        buildOnboardingPath('/signup/select-sectors', {
          email,
          params: { token, registrationType },
        }),
        {
          state: { email, token, registrationType },
          replace: true,
        }
      );
    }, SUCCESS_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [verified, navigate, email, token, registrationType]);

  const clearForm = useCallback(() => {
    setOtp(Array(OTP_LENGTH).fill(''));
    setVerifyFailed(false);
  }, []);

  const handleVerify = useCallback(
    async (codeToVerify?: string) => {
      const code = codeToVerify ?? otpCode;
      if (!hasEmail || code.length !== OTP_LENGTH || verifying || verified) return;

      // Defense in depth: if the email is already marked verified in this tab,
      // do not call the verify endpoint again — even if the backend would
      // (incorrectly) accept the request, the user must not be able to submit
      // another OTP after the account was created.
      if (isEmailMarkedVerified(email)) {
        setVerified(true);
        return;
      }

      setVerifying(true);
      setVerifyFailed(false);
      setResendError(null);
      setResendNotice(null);

      try {
        const result = await verifyOtp(email, code);
        markEmailVerified(email);
        clearSignupInFlight();
        setRegistrationType(result.registration_type);
        setVerified(true);
      } catch {
        setVerifyFailed(true);
      } finally {
        setVerifying(false);
      }
    },
    [email, hasEmail, otpCode, verifying, verified]
  );

  const handleTryAgain = useCallback(() => {
    clearForm();
  }, [clearForm]);

  const handleResend = useCallback(async () => {
    if (!hasEmail || resending || verifying || verified) return;
    setResending(true);
    setResendError(null);
    setResendNotice(null);

    try {
      await resendOtp(email);
      setResendNotice(RESEND_SUCCESS_MESSAGE);
      clearForm();
    } catch (err) {
      if (err instanceof ApiError) {
        setResendError(err.message || DEFAULT_RESEND_ERROR);
      } else {
        setResendError(DEFAULT_RESEND_ERROR);
      }
    } finally {
      setResending(false);
    }
  }, [email, hasEmail, resending, verifying, verified, clearForm]);

  const primaryAction = verifyFailed ? handleTryAgain : () => void handleVerify();
  const showVerifyingState = verifying && !verifyFailed && !verified;
  const primaryLabel = useMemo(() => {
    if (verified) return 'Email Verified';
    if (verifying) return verifyFailed ? 'Try again' : 'Verifying Email';
    return verifyFailed ? 'Try again' : 'Verify';
  }, [verified, verifying, verifyFailed]);

  const primaryVariant = verified ? 'success' : verifyFailed ? 'error' : 'auth';
  const primaryDisabled = verifyFailed
    ? verifying || verified
    : !hasEmail || !isOtpComplete || verifying || verified;

  const subtitleEmail = email || FALLBACK_EMAIL_LABEL;

  return (
    <div className="min-h-screen bg-white flex flex-col items-center pt-[73px] px-4">
      <img
        src={logoImage}
        alt="Learnovate360"
        className="h-[115px] w-[244px] object-contain mb-[35px]"
      />

      <div className="w-[509px] bg-white rounded-[8px] shadow-elevation-1 p-6">
        <Heading as="h2" className="text-[22px] font-medium leading-7 text-black text-center m-0">
          Lets Verify your email!
        </Heading>

        <Paragraph className="mt-2 text-base leading-7 text-gray-body text-center">
          Enter the OTP sent to your email {subtitleEmail}.
        </Paragraph>

        <div className="mt-6">
          <OtpInput
            value={otp}
            onChange={(next) => {
              setOtp(next);
              if (verifyFailed) setVerifyFailed(false);
            }}
            onComplete={handleVerify}
            disabled={verifying || verified || resending}
            hasError={verifyFailed}
          />
        </div>

        <Button
          type="button"
          variant={primaryVariant}
          onClick={primaryAction}
          disabled={primaryDisabled}
          className={cn(
            'mt-5 mx-auto flex w-[320px] h-11 px-4 py-[10px] text-[18px] leading-6 font-medium rounded-[4px]',
            (showVerifyingState || verified) && 'disabled:opacity-100'
          )}
        >
          {showVerifyingState && <Spinner className="h-6 w-6 text-white" />}
          {verified && <Icon src={checkCircleIcon} className="h-6 w-6 text-white" />}
          {primaryLabel}
        </Button>

        <div className="mt-5 text-base leading-7 text-gray-body text-center">
          Didn&rsquo;t get the OTP?{' '}
          <Button
            type="button"
            variant="link"
            onClick={handleResend}
            disabled={!hasEmail || resending || verifying || verified}
            className="inline p-0 h-auto align-baseline text-base leading-7 font-semibold text-brand-primary no-underline hover:no-underline"
          >
            {resending ? 'Sending…' : 'Resend'}
          </Button>
        </div>

        {verifyFailed && (
          <div
            role="alert"
            className="mt-[15px] flex gap-2 items-center p-6 bg-[#fcf1f4] border border-[#f2ada9] rounded-[6px]"
          >
            <Icon src={errorFillIcon} className="text-error-dark w-6 h-6 shrink-0" />
            <Paragraph className="text-[18px] leading-6 font-medium text-black m-0 flex-1">
              {VERIFY_ERROR_MESSAGE}
            </Paragraph>
          </div>
        )}

        <CollapsibleContainer
          isOpen={Boolean(resendError) || Boolean(resendNotice)}
          className="mt-4"
        >
          {resendError && (
            <Alert variant="destructive" onDismiss={() => setResendError(null)}>
              {resendError}
            </Alert>
          )}
          {resendNotice && !resendError && (
            <Alert variant="success" onDismiss={() => setResendNotice(null)}>
              {resendNotice}
            </Alert>
          )}
        </CollapsibleContainer>

        <CollapsibleContainer isOpen={!hasEmail} className="mt-4">
          {!hasEmail && <Alert variant="destructive">{MISSING_EMAIL_MESSAGE}</Alert>}
        </CollapsibleContainer>
      </div>
    </div>
  );
};
